/**
 * The design tokens JavaScript needs as values: the MUI theme, charts, and SVG that is
 * drawn to an image (certificates), where a CSS variable would not resolve. These mirror
 * src/styles/tokens.css; tokens.test.ts fails if the two drift apart. Everything else uses
 * the CSS variables.
 */
export const VISMA = {
  purple: '#7F56FA',
  black: '#131313',
  white: '#FEFEFE',
  creme: '#F9F5F1',
  coolGrey: '#B9C4C9',
  coolGreyLight: '#E7EBEC',
  burgundy: '#4C0C32',
  olive: '#4A4608',
  orange: '#FFAB65',
} as const;

/** Role colours: the brand and its states. */
export const BRAND = VISMA.purple;
export const BRAND_HOVER = '#6A3FE8';
/** Purple text: also passes on Creme (5.55:1), where #7F56FA does not (4.20:1). */
export const BRAND_TEXT = '#6A3FE8';
/** Purple text or a glyph on black (6.9:1 on #131313). */
export const BRAND_ON_INVERSE = '#A98BFF';

/**
 * The Rise's three tints, back to front (logo Rise v4, owner's pick O4): depth without a
 * knockout. On light: two lighter purples and Visma Purple. On black: white at 40%, 67%,
 * 100%. On purple: white at 45%, 72%, 100%. As --color-rise-* in tokens.css.
 */
export const RISE_TINTS = {
  light: ['#C9B8FD', '#A78BFB', VISMA.purple],
  dark: ['#FEFEFE66', '#FEFEFEAA', VISMA.white],
  purple: ['#FEFEFE73', '#FEFEFEB8', VISMA.white],
} as const;

/** Surfaces, text and lines, as --color-surface-*, --color-text-*, --color-border*. */
export const SURFACE = {
  page: '#FEFEFE',
  card: '#FEFEFE',
  field: '#F9F5F1',
  subtle: '#F4F5F6',
  inverse: '#131313',
} as const;

export const TEXT = {
  primary: '#131313',
  secondary: '#475467',
  muted: '#667085',
  onInverse: '#FEFEFE',
  onInverseMuted: '#B9C4C9',
} as const;

export const BORDER = {
  default: '#E7EBEC',
  strong: '#B9C4C9',
  input: '#8A8A8A',
} as const;

/** Charts: the "previous period" line and gridlines. */
export const CHART = { compare: '#7A858C', grid: '#E7EBEC' } as const;

/** Status text colours (the darkest shade of each family), as --color-*-text. */
export const STATUS_TEXT = {
  success: '#1f5132',
  warning: '#7a4a06',
  danger: '#8f1020',
  info: '#1e3a8a',
} as const;

/** Shape, in px, as --radius-*. */
export const RADIUS = { xs: 6, sm: 8, md: 10, card: 12, tile: 16, pill: 99 } as const;

export const ELEVATION_OVERLAY = '0 12px 32px rgba(19, 19, 19, 0.12)';

/** The font stack, as --font-family-body. */
export const FONT_FAMILY = "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif";

/** Motion, in ms, as --dur-*; easings as --ease-*. */
export const DURATION = { instant: 80, fast: 120, base: 180, slow: 240, count: 600 } as const;
export const EASE = {
  standard: 'cubic-bezier(0.2, 0, 0, 1)',
  enter: 'cubic-bezier(0.16, 1, 0.3, 1)',
  exit: 'cubic-bezier(0.4, 0, 1, 1)',
} as const;

/** Data series in order, as --color-series-1..5. */
export const SERIES = [VISMA.purple, VISMA.burgundy, VISMA.olive, VISMA.coolGrey, VISMA.orange] as const;
